import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Phone, TrendingDown, Leaf } from "lucide-react";
import LeadDialog from "@/components/LeadDialog";
import { STATS } from "@/data/content";

const HERO_IMG =
  "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Hero() {
  return (
    <section className="relative bg-white overflow-hidden" data-testid="hero-section">
      {/* soft green backdrop accents */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-lime/5 -z-0" />
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-lime/10 blur-3xl -z-0" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-36 pb-20 lg:pt-44 lg:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <motion.span
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="overline text-brandblue inline-flex items-center gap-2 mb-6 bg-brandblue/10 px-3 py-1.5"
            >
              <TrendingDown className="h-4 w-4" /> Performance énergétique · Bâtiments tertiaires
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] text-midnight"
            >
              Réduisez durablement vos consommations d'énergie grâce au{" "}
              <span className="text-emerald">Contrat de Performance Énergétique.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-7 text-lg text-slate-600 font-light leading-relaxed max-w-2xl"
            >
              Nous accompagnons les entreprises, collectivités et gestionnaires de patrimoine dans leur mise
              en conformité avec le Décret Tertiaire tout en optimisant le financement de leurs travaux
              grâce aux Certificats d'Économies d'Énergie.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <LeadDialog leadType="audit">
                <Button data-testid="hero-audit-btn" className="rounded-none bg-emerald hover:bg-emerald-600 text-white font-medium px-8 py-6 text-base group">
                  <span className="flex items-center gap-2">Demander un audit <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" /></span>
                </Button>
              </LeadDialog>
              <LeadDialog leadType="callback">
                <Button variant="outline" data-testid="hero-callback-btn" className="rounded-none bg-transparent border-midnight text-midnight hover:bg-midnight hover:text-white font-medium px-8 py-6 text-base">
                  <Phone className="h-5 w-5 mr-2" /> Être rappelé
                </Button>
              </LeadDialog>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative overflow-hidden">
              <img src={HERO_IMG} alt="Bâtiment tertiaire à La Défense" className="w-full h-[420px] lg:h-[520px] object-cover" />
              <div className="absolute inset-0 border-8 border-white" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-midnight text-white p-6 max-w-[220px] shadow-xl hidden sm:block">
              <Leaf className="h-6 w-6 text-lime mb-2" />
              <p className="font-display font-bold leading-tight">Réduction durable des consommations</p>
            </div>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55 }}
          className="mt-16 lg:mt-24 grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-200 border border-slate-200"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div key={s.label} className="bg-white p-8">
              <div className="font-display text-4xl lg:text-5xl font-black text-emerald">{s.value}</div>
              <p className="mt-3 text-midnight font-medium">{s.label}</p>
              <p className="text-sm text-slate-500 mt-1">{s.sub}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
